import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiJson } from "../lib/queryClient";
import {
  DRONE_CLASS_INFO,
  DRONE_TYPE_INFO,
  findPresetForDrone,
  getPresetLimits,
} from "@client/lib/dronePresets";

type DroneClass = "C0" | "C1" | "C2" | "C3" | "C4" | "C5" | "C6" | "legacy";
type DroneType = "selfie" | "standard" | "fpv_only" | "hybrid_fpv";

type WeatherLimits = {
  maxWind: number;
  maxGusts: number;
  maxRain: number;
  minTemp: number;
  maxTemp?: number;
  minCloudCeiling: number;
  minVisibility: number;
};

type DroneCompliance = {
  operatorRegistered: boolean;
  eIdLabeledOnDrone: boolean;
  insuranceActive: boolean;
  competencyA1A3: boolean;
  certificateA2: boolean;
  remoteIdConfigured: boolean;
};

type DroneBattery = {
  id: string;
  name: string;
  serialNumber: string;
  createdAt: string;
};

type Drone = {
  id: string;
  name: string;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  alphaTangoId: string | null;
  alphaTangoExpirationDate: string | null;
  classMark: string;
  mtomGrams: number;
  hasCameraOrSensor: boolean;
  hasRemoteId: boolean;
  altitudeLocked120m: boolean;
  charDimensionM: number | null;
  ownedNdFilters: string[] | null;
  batteries: DroneBattery[] | null;
  droneType: string;
  openCategory: string;
  compliance: DroneCompliance;
  limits: WeatherLimits;
};

const DEFAULT_COMPLIANCE: DroneCompliance = {
  operatorRegistered: false,
  eIdLabeledOnDrone: false,
  insuranceActive: false,
  competencyA1A3: false,
  certificateA2: false,
  remoteIdConfigured: false,
};

function getDefaultLimitsForClass(classMark: string): WeatherLimits {
  switch (classMark) {
    case "C0":
      return { maxWind: 8, maxGusts: 10, maxRain: 0.3, minTemp: 5, maxTemp: 40, minCloudCeiling: 120, minVisibility: 500 };
    case "C1":
      return { maxWind: 10, maxGusts: 12, maxRain: 0.5, minTemp: 0, maxTemp: 40, minCloudCeiling: 120, minVisibility: 1000 };
    case "C2":
      return { maxWind: 12, maxGusts: 15, maxRain: 0.5, minTemp: -5, maxTemp: 40, minCloudCeiling: 120, minVisibility: 2000 };
    case "C3":
    case "C4":
      return { maxWind: 15, maxGusts: 18, maxRain: 1.0, minTemp: -10, maxTemp: 40, minCloudCeiling: 120, minVisibility: 5000 };
    case "C5":
    case "C6":
      return { maxWind: 18, maxGusts: 22, maxRain: 1.5, minTemp: -15, maxTemp: 40, minCloudCeiling: 120, minVisibility: 8000 };
    default:
      return { maxWind: 10, maxGusts: 12, maxRain: 0.5, minTemp: 0, maxTemp: 40, minCloudCeiling: 120, minVisibility: 1500 };
  }
}

const ND_OPTIONS = ["ND2", "ND4", "ND8", "ND16", "ND32", "ND64", "ND128", "ND256", "ND400", "ND1000"];

export default function DroneSetup() {
  const { droneId } = useParams<{ droneId: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: drones = [], isLoading } = useQuery<Drone[]>({ queryKey: ["/api/drones"] });
  const drone = drones.find((d) => d.id === droneId);

  const [name, setName] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [model, setModel] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [alphaTangoId, setAlphaTangoId] = useState("");
  const [alphaTangoExpirationDate, setAlphaTangoExpirationDate] = useState("");
  const [classMark, setClassMark] = useState<DroneClass>("C0");
  const [mtomGrams, setMtomGrams] = useState(249);
  const [charDimensionMm, setCharDimensionMm] = useState("");
  const [hasCameraOrSensor, setHasCameraOrSensor] = useState(true);
  const [hasRemoteId, setHasRemoteId] = useState(false);
  const [altitudeLocked120m, setAltitudeLocked120m] = useState(false);
  const [ownedNdFilters, setOwnedNdFilters] = useState<string[]>([]);
  const [batteries, setBatteries] = useState<DroneBattery[]>([]);
  const [droneType, setDroneType] = useState<DroneType>("standard");
  const [openCategory, setOpenCategory] = useState("A1");
  const [compliance, setCompliance] = useState<DroneCompliance>(DEFAULT_COMPLIANCE);
  const [limits, setLimits] = useState<WeatherLimits>(getDefaultLimitsForClass("C0"));
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (drone && !loaded) {
      setName(drone.name);
      setManufacturer(drone.manufacturer || "");
      setModel(drone.model || "");
      setSerialNumber(drone.serialNumber || "");
      setAlphaTangoId(drone.alphaTangoId || "");
      setAlphaTangoExpirationDate(drone.alphaTangoExpirationDate || "");
      setClassMark(drone.classMark as DroneClass);
      setMtomGrams(drone.mtomGrams);
      setCharDimensionMm(
        drone.charDimensionM && drone.charDimensionM > 0 ? String(Math.round(drone.charDimensionM * 1000)) : "",
      );
      setHasCameraOrSensor(drone.hasCameraOrSensor);
      setHasRemoteId(drone.hasRemoteId ?? false);
      setAltitudeLocked120m(drone.altitudeLocked120m ?? false);
      setOwnedNdFilters(drone.ownedNdFilters ?? []);
      setBatteries(drone.batteries ?? []);
      setDroneType(drone.droneType as DroneType);
      setOpenCategory(drone.openCategory);
      setCompliance(drone.compliance ?? DEFAULT_COMPLIANCE);
      setLimits(drone.limits ?? getDefaultLimitsForClass(drone.classMark));
      setLoaded(true);
    }
  }, [drone, loaded]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const charDimM = parseInt(charDimensionMm, 10);
      const payload = {
        name: name.trim(),
        manufacturer: manufacturer.trim() || null,
        model: model.trim() || null,
        serialNumber: serialNumber.trim() || null,
        alphaTangoId: alphaTangoId.trim() || null,
        alphaTangoExpirationDate: alphaTangoExpirationDate.trim() || null,
        classMark,
        mtomGrams,
        charDimensionM: charDimM > 0 ? charDimM / 1000 : null,
        hasCameraOrSensor,
        hasRemoteId,
        altitudeLocked120m,
        ownedNdFilters: ownedNdFilters.length > 0 ? ownedNdFilters : null,
        batteries: batteries.length > 0 ? batteries : null,
        droneType,
        openCategory,
        compliance,
        limits,
      };
      return apiJson("PUT", `/api/drones/${droneId}`, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/drones"] });
      navigate("/drohnen");
    },
  });

  const needsEid = classMark !== "C0" || mtomGrams >= 250 || hasCameraOrSensor;
  const needsRemoteId = classMark === "C1" || classMark === "C2" || classMark === "C3";

  const presetLimits = useMemo(() => {
    const p = findPresetForDrone(name, manufacturer || undefined, model || undefined);
    return p ? getPresetLimits(p) : null;
  }, [name, manufacturer, model]);

  function toggleNd(nd: string) {
    setOwnedNdFilters((prev) => (prev.includes(nd) ? prev.filter((v) => v !== nd) : [...prev, nd]));
  }

  function addBattery() {
    const bName = prompt("Name des Akkus (z.B. Akku #1)")?.trim();
    if (!bName) return;
    const serial = prompt("Seriennummer des Akkus")?.trim() || "";
    setBatteries((prev) => [
      ...prev,
      {
        id: `bat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name: bName,
        serialNumber: serial,
        createdAt: new Date().toISOString(),
      },
    ]);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    saveMutation.mutate();
  }

  if (isLoading) {
    return <div className="text-center text-[var(--color-app-text-secondary)] py-10">Lädt …</div>;
  }

  if (!drone) {
    return (
      <div className="space-y-4">
        <Link to="/drohnen" className="text-sm text-brand-600 hover:underline">
          ← Zurück zu Drohnen
        </Link>
        <div className="card text-center py-12 text-[var(--color-app-text-secondary)]">Drohne nicht gefunden.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="screen-drone-setup">
      <div>
        <Link to="/drohnen" className="text-sm text-brand-600 hover:underline">
          ← Zurück zu Drohnen
        </Link>
        <p className="text-lg font-semibold text-[var(--color-app-text)] mt-2">Drohne bearbeiten</p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <div className="card space-y-4">
          <div>
            <label className="label">Name</label>
            <input
              className="input"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. DJI Mini 4 Pro"
              data-testid="drone-input-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Hersteller</label>
              <input className="input" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} placeholder="z.B. DJI" />
            </div>
            <div>
              <label className="label">Modell</label>
              <input className="input" value={model} onChange={(e) => setModel(e.target.value)} placeholder="z.B. Mini 4 Pro" />
            </div>
          </div>

          <div>
            <label className="label">Seriennummer</label>
            <input
              className="input"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="optional, z.B. 1581F4..."
              data-testid="drone-input-serial"
            />
          </div>

          <div>
            <label className="label">EU-Klasse</label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(DRONE_CLASS_INFO) as DroneClass[]).map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => {
                    setClassMark(c);
                    setLimits(getDefaultLimitsForClass(c));
                  }}
                  className={
                    classMark === c
                      ? "btn-primary text-xs px-3 py-1.5"
                      : "btn-secondary text-xs px-3 py-1.5"
                  }
                  data-testid={`drone-class-${c}`}
                >
                  {DRONE_CLASS_INFO[c].label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Gewicht (MTOM) in g</label>
              <input
                type="number"
                className="input"
                min={0}
                value={mtomGrams}
                onChange={(e) => setMtomGrams(parseInt(e.target.value, 10) || 0)}
              />
            </div>
            <div>
              <label className="label">Char. Dimension (CD) in mm</label>
              <input
                type="number"
                className="input"
                value={charDimensionMm}
                onChange={(e) => setCharDimensionMm(e.target.value)}
                placeholder="z.B. 300"
              />
            </div>
          </div>
          <p className="text-xs text-[var(--color-app-text-tertiary)] -mt-2">
            Multicopter: Diagonale zwischen den Rotorspitzen in ungünstigster Propellerstellung · Starrflügler: Spannweite oder Rumpflänge.
            Grundlage für die VLOS-Planung (EASA Issue 2).
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Typ</label>
              <select className="input" value={droneType} onChange={(e) => setDroneType(e.target.value as DroneType)}>
                {(Object.keys(DRONE_TYPE_INFO) as DroneType[]).map((t) => (
                  <option key={t} value={t}>
                    {DRONE_TYPE_INFO[t].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Kategorie</label>
              <select className="input" value={openCategory} onChange={(e) => setOpenCategory(e.target.value)}>
                {["A1", "A2", "A3"].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="card space-y-1">
          <ToggleRow
            label="Kamera / Sensor für personenbezogene Daten"
            checked={hasCameraOrSensor}
            onChange={setHasCameraOrSensor}
          />
          <ToggleRow
            label="EU Remote-ID integriert"
            description="Remote-ID ist ab 2024 für C1-C3 Pflicht"
            checked={hasRemoteId}
            onChange={setHasRemoteId}
          />
          <ToggleRow
            label="Höhe auf 120 m begrenzt (Firmware)"
            description="Firmware-Lock auf max. 120 m AGL"
            checked={altitudeLocked120m}
            onChange={setAltitudeLocked120m}
          />
        </div>

        <div className="card">
          <h2 className="text-sm font-semibold text-[var(--color-app-text)]">Vorhandene ND-Filter</h2>
          <p className="text-xs text-[var(--color-app-text-tertiary)] mb-3">
            Wähle aus, welche ND-Filter du tatsächlich besitzt. Der ND-Filter Finder schlägt dir dann das beste
            passende Filter aus deinem Bestand vor.
          </p>
          <div className="flex flex-wrap gap-2">
            {ND_OPTIONS.map((nd) => {
              const active = ownedNdFilters.includes(nd);
              return (
                <button
                  type="button"
                  key={nd}
                  onClick={() => toggleNd(nd)}
                  className={
                    active
                      ? "text-xs px-3 py-1.5 rounded-md border border-brand-500 bg-brand-50 text-brand-700 font-semibold"
                      : "text-xs px-3 py-1.5 rounded-md border border-[var(--color-app-border)] text-[var(--color-app-text-secondary)] font-semibold"
                  }
                  data-testid={`nd-chip-${nd}`}
                >
                  {nd}
                </button>
              );
            })}
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-[var(--color-app-text)]">Akkus (optional)</h2>
              <p className="text-xs text-[var(--color-app-text-tertiary)]">Lege Akkus an, um pro Flug Auswahl + Restkapazität zu erfassen.</p>
            </div>
            <button type="button" className="btn-secondary text-xs px-3 py-1.5" onClick={addBattery} data-testid="battery-add-button">
              + Hinzufügen
            </button>
          </div>
          {batteries.length === 0 ? (
            <p className="text-xs text-[var(--color-app-text-tertiary)] italic mt-3">Noch keine Akkus angelegt.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {batteries.map((b) => (
                <li
                  key={b.id}
                  className="flex items-center justify-between gap-3 bg-[var(--color-app-bg-secondary)] rounded-md px-3 py-2"
                  data-testid={`battery-row-${b.id}`}
                >
                  <span className="text-sm font-medium text-[var(--color-app-text)]">
                    {b.name} · SN: {b.serialNumber || "—"}
                  </span>
                  <button
                    type="button"
                    className="text-xs text-[var(--color-nogo)] hover:underline"
                    onClick={() => setBatteries((prev) => prev.filter((x) => x.id !== b.id))}
                    data-testid={`battery-delete-${b.id}`}
                  >
                    Entfernen
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card space-y-1">
          <h2 className="text-sm font-semibold text-[var(--color-app-text)] mb-2">Drohnen-Status</h2>
          {needsEid ? (
            <ToggleRow
              label="e-ID auf der Drohne angebracht"
              description="Pflicht"
              descriptionClass="text-[var(--color-nogo)]"
              checked={compliance.eIdLabeledOnDrone}
              onChange={(v) => setCompliance((p) => ({ ...p, eIdLabeledOnDrone: v }))}
            />
          ) : null}
          {needsRemoteId ? (
            <ToggleRow
              label="Remote-ID konfiguriert"
              description="Pflicht"
              descriptionClass="text-[var(--color-nogo)]"
              checked={compliance.remoteIdConfigured}
              onChange={(v) => setCompliance((p) => ({ ...p, remoteIdConfigured: v }))}
            />
          ) : null}
          <p className="text-xs text-[var(--color-app-text-tertiary)] pt-2">
            Versicherung, LBA-Registrierung und Kompetenznachweise werden im Pilotenprofil verwaltet.
          </p>
        </div>

        <div className="card space-y-4">
          <h2 className="text-sm font-semibold text-[var(--color-app-text)]">Länderspezifische Dokumente</h2>
          <div>
            <label className="label">AlphaTango-Nummer (Frankreich)</label>
            <input
              className="input"
              value={alphaTangoId}
              onChange={(e) => setAlphaTangoId(e.target.value)}
              placeholder="z.B. UAS-FR-XXXXXX"
              data-testid="drone-input-alphatango-id"
            />
          </div>
          <div>
            <label className="label">Ablaufdatum AlphaTango (TT.MM.JJJJ)</label>
            <input
              className="input"
              value={alphaTangoExpirationDate}
              onChange={(e) => setAlphaTangoExpirationDate(e.target.value)}
              placeholder="TT.MM.JJJJ"
              data-testid="drone-input-alphatango-expiry"
            />
          </div>
          <div className="bg-[var(--color-app-bg-secondary)] border border-[var(--color-app-border)] rounded-md px-3 py-2 text-xs text-[var(--color-app-text-secondary)]">
            Datei-Uploads (AlphaTango-Bestätigung, D-Flight QR-Code) sind in der mobilen App verfügbar. Bitte nutze die
            App, um Dokumente als PDF oder Foto zu hinterlegen.
          </div>
        </div>

        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--color-app-text)]">Wettergrenzen</h2>
            {presetLimits ? (
              <button
                type="button"
                className="text-xs text-brand-600 hover:underline"
                onClick={() => setLimits(presetLimits)}
                data-testid="drone-limits-reset"
              >
                Hersteller-Limits
              </button>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <LimitInput label="Max. Wind (m/s)" value={limits.maxWind} onChange={(v) => setLimits((p) => ({ ...p, maxWind: v }))} />
            <LimitInput label="Max. Böen (m/s)" value={limits.maxGusts} onChange={(v) => setLimits((p) => ({ ...p, maxGusts: v }))} />
            <LimitInput label="Min. Temperatur (°C)" value={limits.minTemp} onChange={(v) => setLimits((p) => ({ ...p, minTemp: v }))} />
            <LimitInput label="Max. Temperatur (°C)" value={limits.maxTemp ?? 40} onChange={(v) => setLimits((p) => ({ ...p, maxTemp: v }))} />
          </div>
        </div>

        {saveMutation.error ? (
          <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] rounded-md px-3 py-2">
            {(saveMutation.error as Error).message}
          </div>
        ) : null}

        <div className="flex gap-2">
          <Link to="/drohnen" className="btn-secondary flex-1 text-center">
            Abbrechen
          </Link>
          <button type="submit" className="btn-primary flex-1" disabled={saveMutation.isPending} data-testid="button-save-drone">
            {saveMutation.isPending ? "Speichert …" : "Speichern"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ToggleRow({
  label,
  description,
  descriptionClass,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  descriptionClass?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer py-1.5">
      <span className="min-w-0">
        <span className="block text-sm text-[var(--color-app-text)]">{label}</span>
        {description ? (
          <span className={`block text-xs mt-0.5 ${descriptionClass ?? "text-[var(--color-app-text-tertiary)]"}`}>{description}</span>
        ) : null}
      </span>
      <span className="relative inline-flex shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <span className="block w-[44px] h-[26px] rounded-full bg-[var(--color-app-bg-tertiary)] transition-colors peer-checked:bg-brand-500" />
        <span className="absolute left-0.5 top-0.5 size-[22px] rounded-full bg-white shadow transition-transform peer-checked:translate-x-[18px]" />
      </span>
    </label>
  );
}

function LimitInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type="number"
        className="input"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      />
    </div>
  );
}
